<template>
  <DropDown :trigger="'contextmenu'" :menus="menu">
    <!-- 统一处理右键/点击/拖拽事件  -->
    <LayersOperator
      class="min-h-full"
      :available-levels="availableLevels"
      :drag-level="dragLevel"
      :hide-bar="!!dragOnCollapsed || !isLayersDragging"
      :drag-on="getDragOn"
      :get-index="getDisplayedIndex"
      @click="handleClick"
      @dragstart="handleDragstart"
      @drag="handleDrag"
      @dragend="handleDragend"
      @contextmenu="handleContextmenu"
      @longstay="handleLongstay"
      @hovermove="updateHoverSelect"
      @layers-scroll-end="updateHoverSelect"
      @mouseleave="handleMouseleave"
    >
      <Layer
        v-for="node in uncollapsedFlatShapeTree"
        ref="layerRefs"
        :key="node.key"
        :class="[
          {
            'opacity-50':
              isLayersDragging &&
              (passiveHighlights.has(node.key) ||
                editorStore.selectedIds.includes(node.key)),
            '!bg-[#3B3431]':
              fixedShape(node) &&
              enableEdit &&
              !editorStore.selectedIds.includes(node.key),
            'mt-[10px] !h-[50px] rounded-[8px]': fixedShape(node),
            'dash-border':
              fixedShape(node) &&
              enableEdit &&
              (editorStore.hovered !== node.key ||
                editorStore.selectedIds.includes(node.key)),
            ' opacity-50': node.extra.notCurrSlotWidget ? true : false
          }
        ]"
        :palette="
          isIn(node, RED_TYPES) || RED_TYPES.has(node.extra.tag)
            ? 'red'
            : 'blue'
        "
        :text="
          node.extra.tag !== ShapeType.PhotoAlbumBg
            ? node.extra.name
            : watchfaceStore.description?.watchfaceType === 'portrait'
            ? '人像表盘背景'
            : '相册表盘背景'
        "
        :layerKey="node.key"
        :icon-src="ICON_SRC[node.extra.tag] ?? 'shape-ellipse.svg'"
        :is-group="!!(node.children || expandAllowedShapes.has(node.extra.tag))"
        :is-collapse="node.extra.collapsed"
        :level="node.extra.level"
        :visibility-hidden="
          node.extra.visibility && node.ancestryInfo?.hasInvisible
        "
        :visibility="node.extra.visibility"
        :locked-hidden="!node.extra.locked && node.ancestryInfo?.haslocked"
        :locked="node.extra.locked"
        :status="
          editorStore.selectedIds.includes(node.key)
            ? 'highlight'
            : passiveHighlights.has(node.key)
            ? 'passiveHighlight'
            : 'default'
        "
        :show-border="
          (editorStore.hovered === node.key && !isLayersDragging) ||
          dragOnCollapsed === node.key ||
          (dragParent === node.key && !dragOnCollapsed)
        "
        :show-icon="
          !isLayersDragging && enableEdit && editorStore.hovered === node.key
        "
        :show-collapse-icon="isPanelHovered"
        :disable="{
          lock: fixedShape(node),
          rename:
            (!enableEdit || fixedShape(node)) && !isEditorShapeSlotFollowing,
          lockedChange: !enableEdit || !!Shape.getShape(node.key)?.attrs._maskFlag,
          visibilityChange: !enableEdit || !!Shape.getShape(node.key)?.attrs._maskFlag
        }"
        @locked-change="changeAttr(node.key, '_isLocked', $event)"
        @visibility-change="changeAttr(node.key, '_visibility', $event)"
        @collapse-change="changeAttr(node.key, '_collapsed', $event)"
        @text-change="
          (newText: string | number | boolean) => {
            if (newText) {
              changeAttr(node.key, 'name', newText);
            }
          }
        "
      />

      <!-- 幽灵图榜样 防止被 overflow-hidden -->
      <Teleport to="#app">
        <div
          v-show="isLayersDragging"
          ref="goastRef"
          class="absolute top-0 left-0 font-medium overflow-hidden pointer-events-none opacity-90 rounded-sm border-[1px] box-content border-[#0D84F2]"
          :style="{
            width: `${goastWidth}px`,
            transform: `translate(${
              goastPosition.x - goastPosition.offsetX - 1
            }px , ${goastPosition.y - goastPosition.offsetY - 1}px)`
          }"
        >
          <Layer
            v-for="node in selectedNodes"
            :key="'drag_' + node.key"
            :palette="
              isIn(node, RED_TYPES) || RED_TYPES.has(node.extra.tag)
                ? 'red'
                : 'blue'
            "
            :text="node.extra.name"
            :icon-src="ICON_SRC[node.extra.tag] ?? 'shape-ellipse.svg'"
            :is-group="
              !!(node.children || expandAllowedShapes.has(node.extra.tag))
            "
            :is-collapse="node.extra.collapsed"
            :level="node.extra.level"
            status="highlight"
          />
        </div>
      </Teleport>
    </LayersOperator>
  </DropDown>
  <modal-shape-export ref="exportRef" />
  <modal-template-create ref="templateRef" />
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useEditorStore } from '@/store/use-editor-store';
import { Shape, ShapeType } from '@/services/shape';
import { useGlobalShortcuts } from '@/hooks/use-global-shortcuts';
import { useShapeManage } from '@/hooks/use-shape-manage';
import {
  ICON_SRC,
  RED_TYPES,
  ShapeTreeNode,
  useShapeTree
} from '@/hooks/use-shape-tree';
import { isMac } from '@/utils/platform';
import LayerVue from './Layer.vue';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { useLayerMenu } from '@/hooks/use-layer-menu';
import { useSlotFollow } from '@/hooks/use-slot-follow';
import { Modal } from 'ant-design-vue';

const { judgeIfFollowingSlot } = useSlotFollow();

const props = withDefaults(
  defineProps<{
    isPanelHovered: boolean;
    enableEdit: boolean;
  }>(),
  {
    isPanelHovered: false,
    enableEdit: true
  }
);
const isDarwin = isMac();
const LAYER_HEIGHT = 40;
const DRAG_ON_LIMIT = [0.25, 0.75];
const passiveHighlights = reactive(new Set<string>());

const layerRefs = ref<Array<InstanceType<typeof LayerVue>>>();
const goastRef = ref<HTMLDivElement>();
const availableLevels = ref<Set<number>[]>();
const dragLevel = ref<number>();
const isDragging = ref(false);
const isLayersDragging = ref(false);
const goastWidth = ref(0);
const goastPosition = reactive({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
const dragOnCollapsed = ref<string>('');
const dragParent = ref<string>('');
const menu = ref<DropDownMenuItem[]>([]);

const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();
// 导出表达式
const exportRef = ref();
const {
  expandAllowedShapes,
  flatShapeTree,
  hasPhotoAlbumBg,
  shapeTree,
  uncollapsedFlatShapeTree,
  checkCanSloted,
  checkCanWidgeted,
  copy,
  cut,
  isIn,
  getAvailableMoveLevelsM,
  getElderBrotherNumber,
  getIndex,
  getLastOffspringIndex,
  getNodeById,
  getParent,
  getParentByPosition,
  getSortedIds,
  getUncollapsedNodeById,
  paste
} = useShapeTree(true);
const { moveShape, sloted, unWidgeted, widgeted, unGroup, getWidgetMaxCount } =
  useShapeManage();
const { followParentMove } = useSlotFollow();
watch(
  () => editorStore.selectedIds,
  (cur) => {
    passiveHighlight(cur);
    expandSelectsAncestry(cur);
  }
);

const selectedNodes = computed<ShapeTreeNode[]>(() =>
  getSortedIds(editorStore.selectedIds).map(
    (id) => getNodeById(id) as ShapeTreeNode
  )
);

const fixedShape = (node: ShapeTreeNode) => {
  return [
    ShapeType.PhotoAlbumBg,
    ShapeType.TextWatchface,
    ShapeType.VideoWatchface
  ].includes(node.extra.tag);
};

function getDisplayedIndex(toLayerTop: number): number {
  const layerHeight = 40;
  const photoAlbumBgHeight = 50;
  const gap = 10;
  const index = Math.floor(toLayerTop / layerHeight);

  if (
    hasPhotoAlbumBg.value &&
    index >= uncollapsedFlatShapeTree.value.length - 1
  ) {
    const extra =
      toLayerTop - (uncollapsedFlatShapeTree.value.length - 1) * layerHeight;

    if (extra < gap || extra > gap + photoAlbumBgHeight) {
      return -1;
    } else {
      return uncollapsedFlatShapeTree.value.length - 1;
    }
  } else {
    return index > uncollapsedFlatShapeTree.value.length - 1 ? -1 : index;
  }
}

function updateHoverSelect(positionInfo: LayerOperationInfo) {
  if (positionInfo.index < 0) {
    editorStore.hovered = '';
  } else if (
    editorStore.hovered !==
    uncollapsedFlatShapeTree.value[positionInfo.index].key
  ) {
    editorStore.hovered =
      uncollapsedFlatShapeTree.value[positionInfo.index].key;
  }
}

function handleMouseleave() {
  editorStore.hovered = '';
}

function handleClick(positionInfo: LayerOperationInfo, e: MouseEvent) {
  // 如果dragstart在相册表盘背景和普通图层的gap之间，抬手时也不应选中新元素
  if (isDragging.value) return;

  if (positionInfo.index < 0) {
    editorStore.unselect();
    return;
  }
  // 相册表盘背景不能与其他元素一起选中
  const target = uncollapsedFlatShapeTree.value[positionInfo.index];

  if (
    selectedNodes.value.every((v) => v.key !== target.key) &&
    (e.shiftKey || (isDarwin ? e.metaKey : e.ctrlKey))
  ) {
    const selectedPhotoAlbumBg = selectedNodes.value.some((v) => fixedShape(v));
    if (selectedPhotoAlbumBg && target.extra.tag !== ShapeType.PhotoAlbumBg) {
      return;
    }
    if (
      !selectedPhotoAlbumBg &&
      selectedNodes.value.length &&
      fixedShape(target)
    ) {
      return;
    }
  }

  // shift连续新增，control/command单个增加/减少
  selectLayer(
    target.key,
    e.shiftKey
      ? 'continuous'
      : (isDarwin ? e.metaKey : e.ctrlKey)
      ? 'toggle'
      : 'replace'
  );
}

function handleContextmenu(positionInfo: LayerOperationInfo) {
  if (positionInfo.index < 0) {
    editorStore.unselect();
  } else {
    updateSelection(uncollapsedFlatShapeTree.value[positionInfo.index].key);
  }
  menu.value = contextmenus(
    uncollapsedFlatShapeTree.value[positionInfo.index].key
  );
}

useGlobalShortcuts(
  [
    {
      keyboardKeys: [']'],
      handler: bringToFront
    },
    {
      keyboardKeys: ['['],
      handler: sendToBack
    },
    {
      keyboardKeys: ['CommandOrControl', 'R'],
      handler: () => {
        if (
          selectedNodes.value.length === 1 &&
          !fixedShape(selectedNodes.value[0])
        ) {
          rename();
        }
      }
    },
    {
      keyboardKeys: ['Shift', 'CommandOrControl', 'H'],
      handler: showHide
    },
    {
      keyboardKeys: ['Shift', 'CommandOrControl', 'L'],
      handler: lockUnlock
    },
    {
      keyboardKeys: ['CommandOrControl', 'C'],
      handler: () => copy(selectedNodes.value)
    },
    {
      keyboardKeys: ['CommandOrControl', 'X'],
      handler: () => cut(selectedNodes.value)
    },
    {
      keyboardKeys: ['CommandOrControl', 'V'],
      handler: paste
    },
    {
      keyboardKeys: ['CommandOrControl', 'G'],
      handler: () => {
        if (checkCanWidgeted(selectedNodes.value)) {
          widgeted(
            editorStore.selectedIds,
            editorStore.themeId,
            selectedNodes.value[0].ancestryInfo?.nodes.at(-1)?.key
          );
        }
      }
    },
    {
      keyboardKeys: ['CommandOrControl', 'Backspace'],
      handler: ungroupIfNeeded
    },
    {
      keyboardKeys: ['CommandOrControl', 'Shift', 'G'],
      handler: ungroupIfNeeded
    },
    // 创建为容器
    {
      keyboardKeys: ['Alt', 'CommandOrControl', 'K'],
      handler: () => {
        if (checkCanSloted(selectedNodes.value)) {
          sloted(editorStore.selectedIds, editorStore.themeId);
        }
      }
    }
  ],
  () =>
    props.enableEdit && editorStore.selectedIds.every((id) => getNodeById(id))
);

const { contextMenus } = useLayerMenu({
  isOnCanvas: false,
  handleTemplateModal: (shapeIds: string[]) =>
    templateRef.value.showModal(shapeIds),
  handleExportModal: (shapeId: string) => exportRef.value?.open?.(shapeId),
  handleRename: rename
});

const contextmenus = (id: string) => {
  const shape = Shape.getShape(id);
  // enableEdit只能判断选中的元素，不能判断未选中的，所以这里根据右键获取到的id判断元素是否跟随中
  if (
    !props.enableEdit ||
    shape?.attrs.slotFollowingId ||
    shape?.parent?.attrs.slotFollowingId
  )
    return [];
  return contextMenus.value;
};

function handleDragstart(positionInfo: LayerOperationInfo, e: MouseEvent) {
  isDragging.value = true;
  if (positionInfo.index < 0) {
    editorStore.unselect();
    return;
  }
  const target = uncollapsedFlatShapeTree.value[positionInfo.index];
  // 锁定的元素不允许拖拽排序
  const shape = Shape.getShape(target.key);
  if (shape?.attrs._isLocked) {
    isDragging.value = false;
    return;
  }
  updateSelection(target.key);
  isLayersDragging.value = true;
  editorStore.hovered = '';
  availableLevels.value = getAvailableMoveLevelsM(selectedNodes.value);
  dragLevel.value = target.extra.level;

  // 幽灵图相关设置
  goastWidth.value =
    (layerRefs.value && layerRefs.value[0].$el.offsetWidth) || 320;
  goastPosition.x = e.pageX;
  goastPosition.y = e.pageY;
  goastPosition.offsetX = positionInfo.offsetX;
  goastPosition.offsetY =
    selectedNodes.value.findIndex((node) => node.key === target.key) *
      LAYER_HEIGHT +
    positionInfo.offsetY;
}

function handleDrag(
  cursorIndex: number,
  movingIndex: number,
  level: number,
  e: MouseEvent
) {
  if (!isLayersDragging.value) return;
  // 更新幽灵图位置
  goastPosition.x = e.pageX;
  goastPosition.y = e.pageY;

  // 是否在折叠组偏中间位置
  const intIndex = Math.floor(cursorIndex);
  const target = uncollapsedFlatShapeTree.value[intIndex];

  if (
    cursorIndex - intIndex >= DRAG_ON_LIMIT[0] &&
    cursorIndex - intIndex <= DRAG_ON_LIMIT[1] &&
    target &&
    target.extra.collapsed
  ) {
    dragOnCollapsed.value = target.key;
  } else {
    dragOnCollapsed.value = '';
  }

  // 父亲
  dragParent.value = getParentByPosition(movingIndex, level)?.key ?? '';
}

function handleDragend(index: number, level: number) {
  if (isLayersDragging.value) {
    moveNodes(selectedNodes.value, index, level);
  }

  goastWidth.value = 0;
  setTimeout(() => {
    isDragging.value = false;
    isLayersDragging.value = false;
    dragParent.value = '';
    dragOnCollapsed.value = '';
  }, 20);
}

function moveNodes(nodes: ShapeTreeNode[], index: number, level: number) {
  if (index < 0 || index > uncollapsedFlatShapeTree.value.length) return;
  const parentId = getParentByPosition(index, level)?.key;
  const excepts = [];
  for (let i = 0; i < nodes.length; i++) {
    excepts.push(nodes[i].key);
    const targetIndex = getElderBrotherNumber(index, level, excepts);
    const parentShape = Shape.getShape(parentId ?? '');
    if (parentShape && parentShape.type === 'Slot') {
      const maxWidgetCounts = getWidgetMaxCount(watchfaceStore.deviceType);
      // 排除已经在目标容器内的元素，避免容器内移动时误报
      const newNodesCount = nodes.filter(
        (n) => !parentShape.children.some((child) => child.id === n.key)
      ).length;
      if (parentShape.children.length + newNodesCount > maxWidgetCounts) {
        Modal.warning({
          title: '无法成容器',
          content: `最多支持${maxWidgetCounts}个组成容器，请您调整后再成容器。`,
          okText: '知道了'
        });
        return;
      }
      if (parentShape.attrs.followingGroupName) {
        const widgetIndex = parentShape.children.findIndex(
          (item) => item.id === nodes[i].key
        );
        const followingSlots = followParentMove(parentShape, widgetIndex);
        if (followingSlots.length) {
          for (const followingSlot of followingSlots) {
            if (followingSlot?.shapeId && followingSlot?.themeId)
              moveShape(
                followingSlot?.shapeId,
                followingSlot?.themeId,
                followingSlot?.parentId,
                targetIndex
              );
          }
        }
      }
    }

    moveShape(nodes[i].key, editorStore.themeId, parentId, targetIndex);
  }
}

function handleLongstay(index: number) {
  if (!isLayersDragging.value) return;
  const intIndex = Math.floor(index);
  if (
    index - intIndex < DRAG_ON_LIMIT[0] ||
    index - intIndex > DRAG_ON_LIMIT[1]
  )
    return;

  const target = uncollapsedFlatShapeTree.value[intIndex];

  if (target && target.extra.collapsed) {
    changeAttr(target.key, '_collapsed', false);

    setTimeout(() => {
      // 更新一下可被拖动到的位置
      availableLevels.value = getAvailableMoveLevelsM(selectedNodes.value);
    }, 20);
  }
}

function selectLayer(
  id: string,
  mode: 'replace' | 'toggle' | 'continuous' = 'replace'
) {
  switch (mode) {
    case 'replace':
      editorStore.select([id]);
      break;
    case 'toggle':
      if (editorStore.selectedIds.includes(id)) {
        editorStore.select(
          editorStore.selectedIds.filter((selectId) => selectId !== id)
        );
      } else {
        editorStore.select([...editorStore.selectedIds, id]);
      }
      break;
    case 'continuous':
      if (!editorStore.selectedIds.length) {
        editorStore.select([id]);
      } else {
        const currentIndex = getIndex(
          getNodeById(id) as ShapeTreeNode,
          'flatShapeTree'
        );
        const indices = selectedNodes.value.map((node) =>
          getIndex(node, 'flatShapeTree')
        );
        continuousSelect(
          Math.min(...indices, currentIndex),
          Math.max(...indices, currentIndex)
        );
      }

      break;
    default:
      break;
  }
}

// 连选
function continuousSelect(minIndex: number, maxIndex: number) {
  // 第一个一定包括
  const selections = [flatShapeTree.value[minIndex].key];
  // 第一个的子孙范围超过了选中范围
  const lastOffspringIndex = getLastOffspringIndex(minIndex);
  if (lastOffspringIndex >= maxIndex) {
    editorStore.select(selections);
    return;
  }

  let index = lastOffspringIndex + 1;

  // 选中的范围内如果子孙范围不超的话就选中
  while (index <= maxIndex) {
    const lastOffspringIndex = getLastOffspringIndex(index);
    if (lastOffspringIndex <= maxIndex || index === maxIndex) {
      selections.push(flatShapeTree.value[index].key);
      index = lastOffspringIndex + 1;
    } else {
      index++;
    }
  }

  editorStore.select(selections);
}

function passiveHighlight(parentIds: string[]) {
  // 被动选中子辈
  passiveHighlights.clear();
  for (const parentId of parentIds) {
    const node = getNodeById(parentId);
    if (!node) continue;
    const parentLevel = node.extra.level;
    let index = getIndex(node, 'flatShapeTree') + 1;

    while (
      flatShapeTree.value[index] &&
      flatShapeTree.value[index].extra.level > parentLevel
    ) {
      passiveHighlights.add(flatShapeTree.value[index].key);
      index++;
    }
  }
}

// 如果不可见，展开所有祖先
function expandSelectsAncestry(select: string[]) {
  for (const id of select) {
    const node = getNodeById(id);
    if (!node) continue;
    if (!getUncollapsedNodeById(id) && node.ancestryInfo) {
      for (const ancestry of node.ancestryInfo.nodes) {
        if (ancestry.extra.collapsed) {
          changeAttr(ancestry.key, '_collapsed', false);
        }
      }
    }
  }
}

function changeAttr(
  id: string,
  attrKey: keyof Shape['attrs'] | '_collapsed',
  value: string | number | boolean
) {
  if (attrKey == '_collapsed') {
    editorStore.updateLayerTreeExpandkeys(id, !value ? 'expand' : 'collapse');
  } else {
    Shape.getShape(id)?.setAttrs({ [attrKey]: value });
  }
}

function updateSelection(id: string) {
  // 当前操作位置对应的图层没有包含在选中范围里
  if (!editorStore.selectedIds.includes(id)) {
    selectLayer(id);
  }
}

function bringToFront() {
  const targetNodes = selectedNodes.value.filter(
    (node) => node.extra.tag !== ShapeType.PhotoAlbumBg
  );
  // 每个图层移到相邻同层级中的最前；多选时同层级相对顺序同移动前的相对顺序，非同层级各自处理
  for (let i = targetNodes.length - 1; i >= 0; i--) {
    const node = targetNodes[i];
    const parentNode = getParent(node);
    moveShape(
      node.key,
      editorStore.themeId,
      parentNode?.key,
      parentNode
        ? (parentNode.children?.length ?? 0) - 1
        : shapeTree.value.length - 1
    );
  }
}

function sendToBack() {
  const targetNodes = selectedNodes.value.filter(
    (node) => node.extra.tag !== ShapeType.PhotoAlbumBg
  );
  for (let i = 0; i < targetNodes.length; i++) {
    const node = targetNodes[i];
    const parentNode = getParent(node);

    moveShape(
      node.key,
      editorStore.themeId,
      parentNode?.key,
      !parentNode && hasPhotoAlbumBg.value ? 1 : 0
    );
  }
}

const templateRef = ref();

function rename() {
  // ref 数组并不保证与源数组相同的顺序
  const layer = layerRefs.value?.find(
    (v) => v.$.vnode.key === editorStore.selectedIds[0]
  );
  if (layer) {
    layer.edit();
  }
}

function showHide() {
  // 都是可见的话隐藏，有一个隐藏着的就全可见
  const isVisible = selectedNodes.value.some(
    (layer) => !layer.extra.visibility
  );
  editorStore.selectedIds.forEach((id) =>
    Shape.getShape(id)?.setAttrs({ _visibility: isVisible })
  );
}

function lockUnlock() {
  // 都是锁定的话解锁，有一个开着的就全锁上
  const targets = selectedNodes.value.filter(
    (node) => node.extra.tag !== ShapeType.PhotoAlbumBg
  );

  const isLock = targets.some((layer) => !layer.extra.locked);
  targets.forEach((target) =>
    Shape.getShape(target.key)?.setAttrs({ _isLocked: isLock })
  );
}

const isEditorShapeSlotFollowing = computed(() => {
  return judgeIfFollowingSlot(
    editorStore.activeShape?.attrs.slotFollowingId ?? undefined
  );
});

function getDragOn(positionInfo: LayerOperationInfo) {
  const node = uncollapsedFlatShapeTree.value[positionInfo.index];
  if (!node) return;
  const tag = node.extra.tag;
  const parents = node.ancestryInfo?.nodes || [];
  if (parents.length) {
    // 母元素数组中最后一个是最近上级
    const parentId = parents[parents.length - 1].key;
    if (parentId) {
      const parentShape = Shape.getShape(parentId);
      if (parentShape?.type === ShapeType.Slot) {
        const isSlotFollowing = judgeIfFollowingSlot(
          parentShape?.attrs.slotFollowingId
        );
        if (isSlotFollowing) return false;
      }
    }
  }
  return (
    props.enableEdit &&
    ![
      ShapeType.PhotoAlbumBg,
      ShapeType.TextWatchface,
      ShapeType.VideoWatchface
    ].includes(tag)
  );
}

function ungroupIfNeeded() {
  // 被解组的可能为 组 或 父元素不为容器的表达式
  const needUngroup = selectedNodes.value.filter(
    (item) =>
      item.extra.tag == ShapeType.Group ||
      (item.extra.tag === ShapeType.Widget &&
        getParent(item)?.extra.tag !== ShapeType.Slot)
  );

  if (needUngroup.length) {
    needUngroup.forEach((node) => {
      if (node.extra.tag === ShapeType.Group) {
        unGroup(node.key, editorStore.themeId);
      } else {
        unWidgeted(node.key, editorStore.themeId);
      }
    });
    editorStore.unselect();
  }
}
</script>

<style lang="less" scoped>
.dash-border {
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23D45E38' stroke-width='1' stroke-dasharray='12%2c 12' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
}
</style>
